/**
 * VendorRentalCalendar – Phase 1 + 2
 * Inline calendar card inside the rental listing form.
 * Lets vendors block individual dates or date ranges,
 * then persists them via PATCH /api/rental-availability/{id}/blocked-dates.
 * Also shows booked dates (from confirmed bookings) in blue, read-only.
 */
import React, { useState, useCallback, useEffect } from 'react';
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/dist/style.css';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { CalendarX, CalendarRange, Trash2, Save, Info, Loader2, RefreshCw } from 'lucide-react';
import { toast } from '../ui/sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ────────── helpers ──────────────────────────────────────────────────────────

const toDateStr = (d) => {
  if (!d) return '';
  const date = d instanceof Date ? d : new Date(d);
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const fromDateStr = (s) => {
  if (!s) return null;
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d);
};

/** Expand a range {start, end} into all YYYY-MM-DD strings */
const expandRange = (start, end) => {
  const result = [];
  const s = fromDateStr(start);
  const e = fromDateStr(end);
  if (!s || !e) return result;
  const cur = new Date(s);
  while (cur <= e) {
    result.push(toDateStr(cur));
    cur.setDate(cur.getDate() + 1);
  }
  return result;
};

// ────────── Component ────────────────────────────────────────────────────────

export default function VendorRentalCalendar({ formData, handleChange }) {
  const listingId = formData?.id;

  // Pull existing blocked dates from formData (saved in availability sub-doc)
  const initial = formData?.availability || {};
  const [blockedDates, setBlockedDates] = useState(() =>
    (initial.blockedDates || []).map(fromDateStr).filter(Boolean)
  );
  const [blockedRanges, setBlockedRanges] = useState(() =>
    (initial.blockedRanges || [])
  );
  const [maxAdvanceDays, setMaxAdvanceDays] = useState(
    initial.maxAdvanceBookingDays != null ? String(initial.maxAdvanceBookingDays) : ''
  );

  const [mode, setMode] = useState('single'); // 'single' | 'range'
  const [rangeSelection, setRangeSelection] = useState({ from: undefined, to: undefined });
  const [saving, setSaving] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());

  // ── Booked dates from confirmed bookings (read-only overlay) ───────────────
  const [bookedDates, setBookedDates] = useState([]);
  const [loadingBooked, setLoadingBooked] = useState(false);

  const fetchBookedDates = useCallback(async () => {
    if (!listingId) return;
    setLoadingBooked(true);
    try {
      const token = sessionStorage.getItem('token');
      const now = new Date();
      const start = toDateStr(now);
      const future = new Date(now);
      future.setMonth(future.getMonth() + 3);
      const end = toDateStr(future);
      const res = await fetch(
        `${API_URL}/api/rental-availability/${listingId}/calendar?start=${start}&end=${end}`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      if (!res.ok) return;
      const data = await res.json();
      const booked = Object.entries(data.calendar || {})
        .filter(([, v]) => v.status === 'booked' || (v.availableUnits === 0 && v.status !== 'blocked'))
        .map(([dateStr]) => fromDateStr(dateStr))
        .filter(Boolean);
      setBookedDates(booked);
    } catch {
      // silent — booked overlay is best-effort
    } finally {
      setLoadingBooked(false);
    }
  }, [listingId]);

  useEffect(() => {
    fetchBookedDates();
  }, [fetchBookedDates]);

  // All blocked date objects for DayPicker modifiers
  const allBlockedDates = [
    ...blockedDates,
    ...blockedRanges.flatMap(r => expandRange(r.start, r.end).map(fromDateStr)),
  ];

  // ── Handlers ───────────────────────────────────────────────────────────────

  const handleSingleDayClick = useCallback((day) => {
    if (!day) return;
    const str = toDateStr(day);
    const alreadyBlocked = blockedDates.some(d => toDateStr(d) === str);
    if (alreadyBlocked) {
      setBlockedDates(prev => prev.filter(d => toDateStr(d) !== str));
    } else {
      setBlockedDates(prev => [...prev, day]);
    }
  }, [blockedDates]);

  const handleRangeSelect = useCallback((range) => {
    setRangeSelection(range || { from: undefined, to: undefined });
    if (range?.from && range?.to) {
      const start = toDateStr(range.from);
      const end = toDateStr(range.to);
      setBlockedRanges(prev => {
        // avoid exact duplicate
        const dup = prev.find(r => r.start === start && r.end === end);
        if (dup) return prev;
        return [...prev, { start, end }];
      });
      setRangeSelection({ from: undefined, to: undefined });
      toast.success(`Range blocked: ${start} → ${end}`);
    }
  }, []);

  const removeRange = (idx) => {
    setBlockedRanges(prev => prev.filter((_, i) => i !== idx));
  };

  const clearAll = () => {
    setBlockedDates([]);
    setBlockedRanges([]);
  };

  // ── Save to backend ────────────────────────────────────────────────────────

  const handleSave = async () => {
    if (!listingId) {
      toast.error('Save the listing first, then manage availability.');
      return;
    }
    setSaving(true);
    try {
      const token = sessionStorage.getItem('token');
      const advDays = maxAdvanceDays !== '' ? parseInt(maxAdvanceDays, 10) : null;
      if (advDays !== null && (isNaN(advDays) || advDays <= 0)) {
        toast.error('Advance booking days must be a positive number');
        setSaving(false);
        return;
      }
      // Cross-field: minDuration must not exceed advance booking window
      const minDur = formData?.minimumRentalDuration ? parseInt(formData.minimumRentalDuration, 10) : null;
      if (minDur !== null && minDur < 1) {
        toast.error('Minimum rental duration must be at least 1');
        setSaving(false);
        return;
      }
      if (minDur && advDays && minDur > advDays) {
        toast.error('Minimum rental duration cannot exceed maximum advance booking window');
        setSaving(false);
        return;
      }
      const payload = {
        blocked_dates: blockedDates.map(toDateStr).filter(Boolean),
        blocked_ranges: blockedRanges,
        max_advance_booking_days: advDays !== null && advDays > 0 ? advDays : null,
      };
      const res = await fetch(`${API_URL}/api/rental-availability/${listingId}/blocked-dates`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Save failed');
      }
      const data = await res.json();
      // Also persist into local formData so parent form knows
      handleChange('availability', {
        ...formData.availability,
        blockedDates: payload.blocked_dates,
        blockedRanges: payload.blocked_ranges,
        maxAdvanceBookingDays: advDays,
      });
      toast.success(`Saved: ${data.blocked_dates} dates + ${data.blocked_ranges} ranges blocked`);
    } catch (e) {
      toast.error(e.message || 'Could not save blocked dates');
    } finally {
      setSaving(false);
    }
  };

  // ── UI ─────────────────────────────────────────────────────────────────────

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return (
    <Card data-testid="vendor-rental-calendar-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <CalendarX className="h-4 w-4 text-red-500" />
          Blocked Dates
        </CardTitle>
        <CardDescription className="text-xs">
          Mark dates when this rental is <strong>not available</strong>. Customers won't be able to book on these days.
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-4">

        {/* ── Mode Tabs ── */}
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setMode('single')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
              mode === 'single'
                ? 'bg-red-100 text-red-700 border border-red-300'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
            data-testid="calendar-mode-single"
          >
            <CalendarX className="h-3.5 w-3.5" />
            Block Days
          </button>
          <button
            type="button"
            onClick={() => { setMode('range'); setRangeSelection({ from: undefined, to: undefined }); }}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
              mode === 'range'
                ? 'bg-orange-100 text-orange-700 border border-orange-300'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
            data-testid="calendar-mode-range"
          >
            <CalendarRange className="h-3.5 w-3.5" />
            Block Range
          </button>
        </div>

        {mode === 'range' && (
          <div className="flex items-center gap-2 text-xs text-orange-700 bg-orange-50 border border-orange-200 rounded-md px-3 py-2">
            <Info className="h-3.5 w-3.5 shrink-0" />
            Click a start date, then an end date to block the entire range.
          </div>
        )}

        {/* ── Calendar ── */}
        <div className="flex justify-center">
          {mode === 'single' ? (
            <DayPicker
              mode="multiple"
              selected={blockedDates}
              onSelect={(days) => setBlockedDates(days || [])}
              disabled={[{ before: today }, ...bookedDates]}
              month={currentMonth}
              onMonthChange={setCurrentMonth}
              numberOfMonths={1}
              modifiers={{ blocked: allBlockedDates, booked: bookedDates }}
              modifiersStyles={{
                blocked: {
                  backgroundColor: '#fecaca',
                  color: '#991b1b',
                  borderRadius: '4px',
                },
                booked: {
                  backgroundColor: '#bfdbfe',
                  color: '#1e40af',
                  borderRadius: '4px',
                  cursor: 'not-allowed',
                },
              }}
              styles={{
                caption: { fontSize: '0.85rem' },
                head_cell: { fontSize: '0.75rem' },
                day: { fontSize: '0.8rem' },
              }}
            />
          ) : (
            <DayPicker
              mode="range"
              selected={rangeSelection}
              onSelect={handleRangeSelect}
              disabled={[{ before: today }, ...bookedDates]}
              month={currentMonth}
              onMonthChange={setCurrentMonth}
              numberOfMonths={1}
              modifiers={{ blocked: allBlockedDates, booked: bookedDates }}
              modifiersStyles={{
                blocked: {
                  backgroundColor: '#fecaca',
                  color: '#991b1b',
                  borderRadius: '4px',
                },
                booked: {
                  backgroundColor: '#bfdbfe',
                  color: '#1e40af',
                  borderRadius: '4px',
                  cursor: 'not-allowed',
                },
              }}
              styles={{
                caption: { fontSize: '0.85rem' },
                head_cell: { fontSize: '0.75rem' },
                day: { fontSize: '0.8rem' },
              }}
            />
          )}
        </div>

        {/* ── Legend ── */}
        <div className="flex items-center gap-4 text-xs text-gray-500 flex-wrap">
          <span className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-sm bg-red-200 inline-block border border-red-300" />
            Blocked (by you)
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-sm bg-blue-200 inline-block border border-blue-300" />
            Booked (confirmed)
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-sm bg-white inline-block border border-gray-300" />
            Available
          </span>
          {loadingBooked && (
            <span className="flex items-center gap-1 text-gray-400">
              <RefreshCw className="h-3 w-3 animate-spin" /> Loading bookings…
            </span>
          )}
        </div>

        {/* ── Blocked Dates Summary ── */}
        {blockedDates.length > 0 && (
          <div className="space-y-1">
            <p className="text-xs font-medium text-gray-600">Individual blocked dates ({blockedDates.length}):</p>
            <div className="flex flex-wrap gap-1 max-h-24 overflow-y-auto">
              {blockedDates.map((d) => {
                const str = toDateStr(d);
                return (
                  <Badge
                    key={str}
                    variant="outline"
                    className="text-xs bg-red-50 border-red-200 text-red-700 cursor-pointer hover:bg-red-100"
                    onClick={() => setBlockedDates(prev => prev.filter(x => toDateStr(x) !== str))}
                    data-testid={`blocked-date-${str}`}
                  >
                    {str} ×
                  </Badge>
                );
              })}
            </div>
          </div>
        )}

        {/* ── Blocked Ranges Summary ── */}
        {blockedRanges.length > 0 && (
          <div className="space-y-1">
            <p className="text-xs font-medium text-gray-600">Blocked ranges ({blockedRanges.length}):</p>
            <div className="space-y-1">
              {blockedRanges.map((r, i) => (
                <div
                  key={r.id || r.name || `${r.title || "r"}-${i}`}
                  className="flex items-center justify-between bg-orange-50 border border-orange-200 rounded-md px-3 py-1.5 text-xs"
                  data-testid={`blocked-range-${i}`}
                >
                  <span className="text-orange-700 font-medium">
                    {r.start} → {r.end}
                  </span>
                  <button
                    type="button"
                    onClick={() => removeRange(i)}
                    className="text-orange-500 hover:text-red-600 transition-colors"
                    data-testid={`remove-range-${i}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Booking Rules Summary ── */}
        {(formData?.minimumRentalDuration || formData?.maxAdvanceBookingDays != null) && (
          <div className="pt-3 border-t border-gray-100 space-y-1" data-testid="booking-rules-summary">
            <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Booking Rules</p>
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-600">
              {formData?.minimumRentalDuration > 0 && (
                <span>
                  Min booking: <strong>{formData.minimumRentalDuration} {formData?.rentalDurationUnit === 'hour' ? 'hour(s)' : 'day(s)'}</strong>
                </span>
              )}
              {maxAdvanceDays && Number(maxAdvanceDays) > 0 && (
                <span>
                  Advance window: <strong>up to {maxAdvanceDays} days</strong>
                </span>
              )}
            </div>
          </div>
        )}

        {/* ── Max Advance Booking Days ── */}
        <div className="pt-3 border-t border-gray-100 space-y-2">
          <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wide">
            Allow Advance Booking Up To (Days)
          </label>
          <div className="flex items-center gap-2">
            <input
              type="number"
              min="1"
              max="3650"
              value={maxAdvanceDays}
              onChange={(e) => setMaxAdvanceDays(e.target.value)}
              placeholder="e.g. 90  (leave blank for unlimited)"
              className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-gray-400 focus:border-gray-400"
              data-testid="max-advance-booking-days-input"
            />
            {maxAdvanceDays && Number(maxAdvanceDays) > 0 && (
              <span className="text-xs text-gray-500 whitespace-nowrap">
                up to {maxAdvanceDays} days
              </span>
            )}
          </div>
          <p className="text-xs text-gray-400">
            Customers cannot book more than this many days in advance. Leave blank to allow unlimited advance booking.
          </p>
        </div>

        {/* ── Action Buttons ── */}
        <div className="flex items-center justify-between pt-2 border-t border-gray-100">
          {(blockedDates.length > 0 || blockedRanges.length > 0) ? (
            <button
              type="button"
              onClick={clearAll}
              className="text-xs text-gray-400 hover:text-red-500 transition-colors flex items-center gap-1"
              data-testid="clear-all-blocks"
            >
              <Trash2 className="h-3.5 w-3.5" />
              Clear all
            </button>
          ) : (
            <span className="text-xs text-gray-400">No dates blocked</span>
          )}

          <Button
            type="button"
            size="sm"
            onClick={handleSave}
            disabled={saving || !listingId}
            data-testid="save-blocked-dates-btn"
            className="flex items-center gap-1.5"
          >
            {saving ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Save className="h-3.5 w-3.5" />
            )}
            {saving ? 'Saving…' : 'Save Blocked Dates'}
          </Button>
        </div>

        {!listingId && (
          <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 flex items-center gap-1.5">
            <Info className="h-3.5 w-3.5 shrink-0" />
            Save the listing first to persist blocked dates.
          </p>
        )}
      </CardContent>
    </Card>
  );
}
